import { useForm } from "react-hook-form";
import { Box, chakra, Flex, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import { EsgLabel, FormInput, NumericFormInput } from "../../../../../../../components/Forms";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { translateSubElementString } from "../../../../../../../utils";
import { mapData } from "./utils";
import {
  Answer
} from "../../../../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";

const categoryOneAttributes = ["calculationMethods", "carbonEmission", "dataCollectionMethods", "howVerifyData",
  "materialityThresholdForCollectingSuppliers", "percentageOfSuppliersHasCollectedData", "reasonExclusion"];

function CategoryOne({ tabKey, currentElement, callMethod, isPreview }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierQuestionnaire.scopeThree"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const formDefaultValues = mapData(categoryOneAttributes, currentElement);
  formDefaultValues["supplyChainEmissionCoverageExplanation"] = currentElement?.["supplyChainEmissionCoverageExplanation"];

  const [included, setIncluded] = useState("yes");
  const [wholeSupplyChainCoveredByEmissionsData, setWholeSupplyChainCoveredByEmissionsData] =
    useState("yes");

  useEffect(() => {
    if (currentElement) {
      setIncluded(currentElement.included === true ? "yes" : "no");
      setWholeSupplyChainCoveredByEmissionsData(currentElement.wholeSupplyChainCoveredByEmissionsData === true ?
        "yes" : "no");
    }
  }, [currentElement]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
    setValue
  } = useForm({
    defaultValues: formDefaultValues
  });

  const onSubmit = (data) => {
    if (!isPreview) {
      const apiData = mapData(categoryOneAttributes, data);
      apiData.included = included === "yes";
      if (apiData.included) {
        apiData.wholeSupplyChainCoveredByEmissionsData = wholeSupplyChainCoveredByEmissionsData === "yes";
        if (wholeSupplyChainCoveredByEmissionsData === "no") {
          apiData.supplyChainEmissionCoverageExplanation = data.supplyChainEmissionCoverageExplanation;
        }
      }
      apiData.categoryKey = tabKey;
      if (isDirty || apiData.included !== currentElement?.included ||
        apiData.wholeSupplyChainCoveredByEmissionsData !== currentElement?.wholeSupplyChainCoveredByEmissionsData) {
        callMethod(apiData);
      } else {
        callMethod(null);
      }
      reset({});
    }
  };

  return (
    <chakra.form
      id={`navForm${tabKey}`}
      onSubmit={handleSubmit(onSubmit)}
      flexGrow="1"
      overflowY="auto"
      overflowX="visible"
      paddingBlockEnd="4"
      paddingInline="2"
      marginInline="-2"
      height="calc(100% - var(--chakra-space-20))"
    >
      <Flex direction="column" marginBlockStart={5} gap={5}>
        <Flex direction="column">
          {isPreview ?
            <Box>
              <EsgLabel text={t("includeQuestion", { activity: t("categories.categoryOne.activity") })} />
              <Answer
                answerText={included ?
                  t(`buttons.${included}`) : ""}
              />
            </Box> :
            <>
              <Text fontSize="sm" opacity="0.75">
                {t("includeQuestion", { activity: t("categories.categoryOne.activity") })}
              </Text>
              <RadioGroup value={included} mb={5} style={{ position: "relative" }}>
                <Stack spacing={5} direction="row">
                  <Radio
                    colorScheme="customScheme"
                    value="yes"
                    onChange={e => {
                      setIncluded(e?.target.value);
                    }}
                  >
                    {t("buttons.yes")}
                  </Radio>
                  <Radio
                    colorScheme="customScheme"
                    value="no"
                    onChange={e => {
                      setIncluded(e?.target.value);
                    }}
                  >
                    {t("buttons.no")}
                  </Radio>
                </Stack>
              </RadioGroup>
            </>
          }
        </Flex>

        <Flex direction="column" gap={5}>
          {included === "yes" ?
            isPreview ?
              <>
                <Box>
                  <EsgLabel marginBlockStart={5}
                            text={translateSubElementString("supplierQuestionnaire.scopeThree.emissions")} />
                  <Answer answerText={formDefaultValues.carbonEmission?.toString()} />
                </Box>
                <Box>
                  <EsgLabel marginBlockStart={5}
                            text={t("categories.categoryOne.suppliersPercentage")} />
                  <Answer answerText={formDefaultValues.percentageOfSuppliersHasCollectedData?.toString()} />
                </Box>
                <Box>
                  <EsgLabel marginBlockStart={5}
                            text={t("categories.categoryOne.wholeSupplyChainCoveredByEmissionsData")} />
                  <Answer answerText={wholeSupplyChainCoveredByEmissionsData ?
                    t(`buttons.${wholeSupplyChainCoveredByEmissionsData}`) : ""} />
                </Box>
                {wholeSupplyChainCoveredByEmissionsData === "no" ?
                  <Box>
                    <EsgLabel marginBlockStart={5}
                              text={t("categories.categoryOne.supplyChainEmissionCoverageExplanation")} />
                    <Answer answerText={formDefaultValues.supplyChainEmissionCoverageExplanation} />
                  </Box> : null
                }
                <Box>
                  <EsgLabel marginBlockStart={5}
                            text={t("categories.categoryOne.materialityThreshold")} />
                  <Answer answerText={formDefaultValues.materialityThresholdForCollectingSuppliers} />
                </Box>
                <Box>
                  <EsgLabel marginBlockStart={5}
                            text={t("categories.categoryOne.collectDataMethods")} />
                  <Answer answerText={formDefaultValues.dataCollectionMethods} />
                </Box>
                <Box>
                  <EsgLabel marginBlockStart={5}
                            text={t("calculationMethods")} />
                  <Answer answerText={formDefaultValues.calculationMethods} />
                </Box>
                <Box>
                  <EsgLabel marginBlockStart={5}
                            text={t("howVerifyData")} />
                  <Answer answerText={formDefaultValues.howVerifyData} />
                </Box>
              </> :
              <>
                <NumericFormInput
                  label={translateSubElementString("supplierQuestionnaire.scopeThree.emissions")}
                  errors={errors}
                  {...register("carbonEmission")}
                  maxInlineSize={{ base: "full", lg: "lg" }}
                />
                <NumericFormInput
                  label={t("categories.categoryOne.suppliersPercentage")}
                  errors={errors}
                  {...register("percentageOfSuppliersHasCollectedData")}
                  maxInlineSize={{ base: "full", lg: "lg" }}
                />
                <Box>
                  <Text fontSize="xs" opacity="0.75" fontWeight="500">
                    {t("categories.categoryOne.wholeSupplyChainCoveredByEmissionsData")}
                  </Text>
                  <RadioGroup value={wholeSupplyChainCoveredByEmissionsData} mb={5} style={{ position: "relative" }}>
                    <Stack spacing={5} direction="row">
                      <Radio
                        colorScheme="customScheme"
                        value="yes"
                        onChange={e => {
                          setValue("supplyChainEmissionCoverageExplanation", "", { shouldDirty: true });
                          setWholeSupplyChainCoveredByEmissionsData(e?.target.value);
                        }}
                      >
                        {t("buttons.yes")}
                      </Radio>
                      <Radio
                        colorScheme="customScheme"
                        value="no"
                        onChange={e => {
                          setWholeSupplyChainCoveredByEmissionsData(e?.target.value);
                        }}
                      >
                        {t("buttons.no")}
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </Box>
                {wholeSupplyChainCoveredByEmissionsData === "no" ?
                  <FormInput
                    label={`${t("categories.categoryOne.supplyChainEmissionCoverageExplanation")} *`}
                    errors={errors}
                    {...register("supplyChainEmissionCoverageExplanation", {
                      required: wholeSupplyChainCoveredByEmissionsData === "no" ?
                        globalTranslation("errors.required") : false
                    })}
                    maxInlineSize={{ base: "full", lg: "lg" }}
                  /> : null}
                <FormInput
                  label={t("categories.categoryOne.materialityThreshold")}
                  errors={errors}
                  {...register("materialityThresholdForCollectingSuppliers")}
                  maxInlineSize={{ base: "full", lg: "lg" }}
                />
                <FormInput
                  label={t("categories.categoryOne.collectDataMethods")}
                  errors={errors}
                  {...register("dataCollectionMethods")}
                  maxInlineSize={{ base: "full", lg: "lg" }}
                />
                <FormInput
                  label={t("calculationMethods")}
                  errors={errors}
                  {...register("calculationMethods")}
                  maxInlineSize={{ base: "full", lg: "lg" }}
                />
                <FormInput
                  label={t("howVerifyData")}
                  errors={errors}
                  {...register("howVerifyData")}
                  maxInlineSize={{ base: "full", lg: "lg" }}
                />
              </>
            :
            isPreview ?
              <Box>
                <EsgLabel marginBlockStart={5}
                          text={t("reasonForExclusion")} />
                <Answer answerText={formDefaultValues.reasonExclusion} />
              </Box> :
              <FormInput
                label={t("reasonForExclusion")}
                errors={errors}
                {...register("reasonExclusion")}
                maxInlineSize={{ base: "full", lg: "lg" }}
              />
          }
        </Flex>
      </Flex>
    </chakra.form>
  );
}

export default CategoryOne;